import { Box, useBreakpointValue } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

type DraggableProgressbarProps = {
  completedPercentage: number;
  bufferedPercentage: number;
  duration: number;
  onSeek: (time: number) => void;
  isFullScreen: boolean;
};

const DraggableProgressbar: React.FC<DraggableProgressbarProps> = ({
  completedPercentage,
  bufferedPercentage,
  duration,
  onSeek,
  isFullScreen
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [hoverPosition, setHoverPosition] = useState<number | null>(null);
  const [dragPosition, setDragPosition] = useState<number>(completedPercentage);
  const progressBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDragging) {
      setDragPosition(completedPercentage);
    }
  }, [completedPercentage, isDragging]);

  const calculatePercentage = (clientX: number): number => {
    if (!progressBarRef.current) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    const position = clientX - rect.left;
    const percentage = (position / rect.width) * 100;
    return Math.min(Math.max(percentage, 0), 100);
  };

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    const clientX =
      "touches" in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const percentage = calculatePercentage(clientX);
    setDragPosition(percentage);
  };

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    const clientX =
      "touches" in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const percentage = calculatePercentage(clientX);
    setHoverPosition(percentage);

    if (isDragging) {
      setDragPosition(percentage);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      const seekTime = (dragPosition / 100) * duration;
      onSeek(seekTime);
      setIsDragging(false);
    }
  };

  const handleMouseLeave = () => {
    setHoverPosition(null);
    if (isDragging) {
      const seekTime = (dragPosition / 100) * duration;
      onSeek(seekTime);
      setIsDragging(false);
    }
  };

  const formatTime = (percentage: number): string => {
    const totalSeconds = Math.floor((percentage / 100) * duration);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  let isMobileDevice = useBreakpointValue({base: true, md: false})

  return (
    <Box
      mx={isFullScreen ? { base: '4%', md: 'unset' } : 'unset'}
      pos={'relative'}
      height={{base: '6px', md: '4px'}}
      _hover={isMobileDevice ? {} : {height: '8px'}}
      width={'100%'}
      id={'progressBarTrack'}
      cursor={'pointer'}
      bgColor={'#FFFFFF33'}
      // className='group relative h-[3px] w-full cursor-pointer'
      ref={progressBarRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      // for mobile devices
      onTouchStart={handleMouseDown}
      onTouchMove={handleMouseMove}
      onTouchEnd={handleMouseLeave}
    >
      {/* <Box pos={'absolute'} h={'100%'} w={'100%'} bgColor={'#ffffff33'}
      // className='absolute h-full w-full bg-[#ffffff33]' 
      /> */}
      {/* <Box pos={'absolute'} bgColor={'#ffffff66'}
        // className='absolute h-full bg-[#ffffff66]'
        style={{ width: `${bufferedPercentage}%` }}
      /> */}
      <Box
        id={'progressBarMoving'}
        pos={'relative'} zIndex={'10'} 
        height={{base: '6px', md: '4px'}}
        _hover={isMobileDevice ? {} : {height: '8px'}}
        bgColor={'#CF4520'}
        // className=' relative z-10 h-full bg-red-error'
        style={{
          width: `${isDragging ? dragPosition : completedPercentage}%`,
        }}
        >
        {isFullScreen ? 
        <Box
          display={{base: 'block', md: 'none'}}
          position="absolute"
          right="-1"
          top="50%"
          h="12px"
          w="12px"
          transform="translateY(-50%)"
          borderRadius="full"
          bgColor={'#CF4520'} /> : null}
      </Box>
      {!isMobileDevice ?
        <style>
        {`
          #progressBarTrack:hover #progressBarMoving {height: 8px}
          #progressBarMoving:hover #progressBarTrack {height: 8px}`
        }
        </style> : null}

      {(hoverPosition !== null || isDragging) && (
        <>
          <Box
            // className='absolute bottom-6 z-20 -translate-x-1/2 transform rounded bg-black px-2 py-1 text-xs text-white'
            position="absolute"
            bottom="6"
            zIndex="20"
            transform="translateX(-50%)"
            borderRadius="md"
            bg="black"
            px="2"
            py="1"
            fontSize="xs"
            color="white"
            left={`${isDragging ? dragPosition : hoverPosition}%`}
          >
            {formatTime(isDragging ? dragPosition : hoverPosition)}
          </Box>
        </>
      )}
      <Box
        position="absolute"
        top="-1"
        h="2"
        w="full"
        opacity={0}
        transition="opacity 0.2s"
        _groupHover={{ opacity: 1 }}
      />
    </Box>
  );
};

export default DraggableProgressbar;
